export async function systemColors() {
    const container = document.querySelector('[data-system-colors]');

    if (!container) return;

    const cssUrl = container.dataset.variablesCss;

    if (!cssUrl) return;

    try {
        const response = await fetch(cssUrl);

        if (!response.ok) {
            throw new Error(`Could not load ${cssUrl}`);
        }

        const css = await response.text();
        const colors = getColorVariables(css);
        const groups = groupColors(colors);

        container.innerHTML = '';

        Object.entries(groups).forEach(([groupName, colors]) => {
            const group = document.createElement('section');
            const title = document.createElement('h2');
            const grid = document.createElement('div');

            group.classList.add('system-colors__group');
            title.classList.add('system-colors__title');
            grid.classList.add('system-colors__grid');

            title.textContent = `${formatLabel(groupName)} (${colors.length})`;

            colors.forEach(color => {
                grid.appendChild(createColorCard(color));
            });

            group.appendChild(title);
            group.appendChild(grid);

            container.appendChild(group);
        });

    } catch (error) {
        console.error('System colors:', error);
    }
}

function getColorVariables(css) {
    const regex = /(--color-[\w-]+)\s*:\s*([^;]+);/g;
    const colors = [];
    const seen = new Set();

    let match;

    while ((match = regex.exec(css)) !== null) {
        const variable = match[1].trim();
        const value = match[2].trim();

        if (seen.has(variable)) continue;

        seen.add(variable);

        colors.push({
            variable,
            value
        });
    }

    return colors;
}

function groupColors(colors) {
    return colors.reduce((groups, color) => {
        const meta = getVariableMeta(color.variable);

        if (!groups[meta.group]) {
            groups[meta.group] = [];
        }

        groups[meta.group].push(color);

        return groups;
    }, {});
}

function getColorFormat(value) {
    const normalized = value.trim().toLowerCase();

    if (normalized.startsWith('#')) return 'HEX';
    if (normalized.startsWith('hsl(') || normalized.startsWith('hsla(')) return 'HSL';
    if (normalized.startsWith('rgb(') || normalized.startsWith('rgba(')) return 'RGB';
    if (normalized.startsWith('oklch(')) return 'OKLCH';
    if (normalized.startsWith('oklab(')) return 'OKLAB';
    if (normalized.startsWith('lch(')) return 'LCH';
    if (normalized.startsWith('lab(')) return 'LAB';
    if (normalized.startsWith('color(')) return 'COLOR';
    if (normalized.startsWith('var(')) return 'VAR';

    // Voor jouw losse HSL channels, zoals:
    // --color-neutral: 0 0% 0%;
    if (/^-?\d+(\.\d+)?\s+-?\d+(\.\d+)?%\s+-?\d+(\.\d+)?%/.test(normalized)) {
        return 'HSL';
    }

    return 'CSS';
}

function createColorCard(color) {
    const meta = getVariableMeta(color.variable);
    const resolvedColor = resolveColor(color.variable);
    const hex = colorToHex(resolvedColor);
    const opacity = getOpacity(resolvedColor);
    const format = getColorFormat(color.value);

    const card = document.createElement('article');

    card.classList.add('system-color');

    card.innerHTML = `
        <div
            class="system-color__swatch"
            style="--system-color: ${resolvedColor}"
        ></div>

        <div class="system-color__header">
            <span class="system-color__category">
                ${formatLabel(meta.group)}
            </span>

            <h3 class="system-color__name">
                ${formatLabel(meta.name)}
            </h3>
        </div>

        <code class="system-color__variable">
            ${color.variable}
        </code>

        <code class="system-color__value">
            ${color.value}
        </code>
    `;
    return card;
}

function getVariableMeta(variable) {
    const parts = variable
        .replace('--color-', '')
        .split('-');

    const group = parts.shift() || 'other';
    const name = parts.join('-') || group;

    return {
        group,
        name
    };
}

function formatLabel(value) {
    return value
        .replace(/-/g, ' ')
        .replace(/\b\w/g, character => character.toUpperCase());
}

function resolveColor(variable) {
    const rootStyles = getComputedStyle(document.documentElement);

    const rawValue = rootStyles
        .getPropertyValue(variable)
        .trim();

    if (!rawValue) {
        return 'transparent';
    }

    const isHslChannels =
        /^-?\d+(\.\d+)?\s+-?\d+(\.\d+)?%\s+-?\d+(\.\d+)?%$/.test(rawValue);

    const color = isHslChannels
        ? `hsl(${rawValue})`
        : `var(${variable})`;

    const element = document.createElement('span');

    element.style.position = 'absolute';
    element.style.visibility = 'hidden';
    element.style.pointerEvents = 'none';
    element.style.backgroundColor = color;

    document.body.appendChild(element);

    const resolved = getComputedStyle(element).backgroundColor;

    element.remove();

    return resolved;
}

function colorToHex(color) {
    const rgba = colorToRgba(color);

    if (!rgba) return color;

    const { r, g, b } = rgba;

    const hex = [r, g, b]
        .map(value => {
            return Math.round(value)
                .toString(16)
                .padStart(2, '0');
        })
        .join('')
        .toUpperCase();

    return `#${hex}`;
}

function getOpacity(color) {
    const rgba = colorToRgba(color);

    if (!rgba) return 100;

    return Math.round(rgba.a * 100);
}

function colorToRgba(color) {
    const rgbMatch = color.match(
        /rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:\s*[,/]\s*([\d.]+))?\s*\)/
    );

    if (rgbMatch) {
        return {
            r: Number(rgbMatch[1]),
            g: Number(rgbMatch[2]),
            b: Number(rgbMatch[3]),
            a: rgbMatch[4] !== undefined
                ? Number(rgbMatch[4])
                : 1
        };
    }

    const srgbMatch = color.match(
        /color\(srgb\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*([\d.]+))?\)/
    );

    if (srgbMatch) {
        return {
            r: Number(srgbMatch[1]) * 255,
            g: Number(srgbMatch[2]) * 255,
            b: Number(srgbMatch[3]) * 255,
            a: srgbMatch[4] !== undefined
                ? Number(srgbMatch[4])
                : 1
        };
    }

    return null;
}